import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { TIER_THRESHOLDS, SUBSCRIPTION_PRICES, DNET_DEX_URL, formatDnetAmount, getTierLabel, getTierIcon, hasWatcherAccess, hasDeepDiverAccess } from '../utils/tiers';
import { trackEvent } from '../utils/analytics';
import CryptoSubscriptionPay from './CryptoSubscriptionPay';

interface Props {
  /** Which tier the user is trying to reach. Closes when null. */
  target: 'watcher' | 'deep_diver' | null;
  onClose: () => void;
  onSubscribe: (tier: 'watcher' | 'deep_diver') => void;
  subscribing: 'watcher' | 'deep_diver' | null;
  /** Skip past the dnet/crypto/card chooser and open straight on the
   *  CryptoSubscriptionPay screen. Used when restoring from a Phantom
   *  deeplink-tx round-trip — TiersPage knows the user was mid-payment
   *  for crypto, so we shouldn't make them re-pick the method. */
  defaultShowCryptoPay?: boolean;
}

/**
 * Step 2 of the upgrade flow: once the user has chosen which tier, ask
 * which payment path they prefer. Three explicit affordances:
 *   1. Hold $DNET — perpetual access, no recurring charge.
 *   2. Crypto monthly — USDC/SOL, pay 30 days at a time. (Coming soon.)
 *   3. Card subscription — Stripe, recurring monthly.
 *
 * Smart-default highlight: $DNET if you already hold any, fiat otherwise.
 */
const UpgradePicker: React.FC<Props> = ({ target, onClose, onSubscribe, subscribing, defaultShowCryptoPay = false }) => {
  const { user } = useAuth();
  const [showCryptoPay, setShowCryptoPay] = useState(defaultShowCryptoPay);

  // Reset whenever the target changes (open / re-open). Honour the
  // defaultShowCryptoPay prop on (re-)open so a deeplink-tx round-trip
  // restores the right view rather than dropping the user back at the
  // method picker.
  useEffect(() => { setShowCryptoPay(defaultShowCryptoPay); }, [target, defaultShowCryptoPay]);

  useEffect(() => {
    if (target) trackEvent('upgrade_picker_shown', { tier: target });
  }, [target]);

  // Esc to dismiss.
  useEffect(() => {
    if (!target) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [target, onClose]);

  if (!target) return null;

  const dnetBalance = user?.dnetBalance ?? 0;
  const threshold = TIER_THRESHOLDS[target];
  const subPrice = SUBSCRIPTION_PRICES[target];
  const monthlyCryptoUsd = subPrice; // same monthly cost as the fiat sub
  const tierLabel = getTierLabel(target);
  const tierIcon = getTierIcon(target);
  const alreadyAtTierViaDnet = target === 'watcher' ? hasWatcherAccess(user?.dnetTier) : hasDeepDiverAccess(user?.dnetTier);
  const dnetGap = Math.max(0, threshold - dnetBalance);

  // Default highlight.
  const recommended: 'dnet' | 'crypto' | 'card' = dnetBalance > 0 ? 'dnet' : 'card';

  const optionClasses = (key: 'dnet' | 'crypto' | 'card') =>
    `w-full text-left rounded-lg border p-4 transition-colors ${
      recommended === key
        ? 'border-crypto-accent/60 bg-crypto-accent/5 hover:bg-crypto-accent/10'
        : 'border-gray-700 bg-crypto-dark/40 hover:border-gray-500'
    }`;

  const handleHoldDnet = () => {
    trackEvent('upgrade_picker_choice', { tier: target, method: 'hold_dnet' });
    trackEvent('buy_dnet_clicked', { source: 'upgrade_picker', tier: target });
    window.open(DNET_DEX_URL, '_blank', 'noopener,noreferrer');
  };

  const handleCryptoMonthly = () => {
    trackEvent('upgrade_picker_choice', { tier: target, method: 'crypto_monthly' });
    setShowCryptoPay(true);
  };

  const handleCard = () => {
    trackEvent('upgrade_picker_choice', { tier: target, method: 'card' });
    onSubscribe(target);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative bg-crypto-gray border border-gray-700 rounded-xl w-full max-w-lg shadow-2xl">
        <div className="p-6 border-b border-gray-800 flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <span>{tierIcon}</span> {showCryptoPay ? `Pay USDC for ${tierLabel}` : `Upgrade to ${tierLabel}`}
            </h2>
            <p className="text-gray-400 text-sm mt-1">{showCryptoPay ? 'One month, no auto-renewal.' : "Choose how you'd like to pay."}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white p-1" aria-label="Close">
            <i className="fas fa-times" />
          </button>
        </div>

        {showCryptoPay ? (
          <div className="p-6">
            <CryptoSubscriptionPay
              tier={target}
              onCancel={() => setShowCryptoPay(false)}
              onPaid={() => {/* keep modal open so the user sees the success block; close via X */}}
            />
          </div>
        ) : (

        <div className="p-6 space-y-3">
          {/* Hold $DNET */}
          <button onClick={handleHoldDnet} className={optionClasses('dnet')} disabled={alreadyAtTierViaDnet}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-white font-medium">Hold {formatDnetAmount(threshold)} $DNET</p>
                <p className="text-gray-400 text-xs mt-1">
                  Perpetual {tierLabel} access while you hold. No subscription, no recurring charge.
                </p>
                <p className="text-gray-500 text-xs mt-1">
                  {alreadyAtTierViaDnet
                    ? `You already hold ${formatDnetAmount(dnetBalance)} — qualifies.`
                    : dnetBalance > 0
                      ? `You hold ${formatDnetAmount(dnetBalance)}.`
                      : 'No $DNET in your wallet yet.'}
                </p>
              </div>
              <span className="text-crypto-accent text-sm font-medium whitespace-nowrap">
                {alreadyAtTierViaDnet
                  ? '✓ Qualifies'
                  : `Buy ${formatDnetAmount(dnetGap)}${dnetBalance > 0 ? ' more' : ''} $DNET →`}
              </span>
            </div>
          </button>

          {/* Crypto monthly */}
          <button onClick={handleCryptoMonthly} className={optionClasses('crypto')}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-white font-medium">Pay monthly with USDC</p>
                <p className="text-gray-400 text-xs mt-1">
                  30 days of {tierLabel} for {monthlyCryptoUsd} USDC on Solana. No auto-renew — pay again to extend.
                </p>
              </div>
              <span className="text-crypto-accent text-sm font-medium whitespace-nowrap">Pay →</span>
            </div>
          </button>

          {/* Card subscription */}
          <button
            onClick={handleCard}
            disabled={subscribing !== null}
            className={optionClasses('card') + ' disabled:opacity-50'}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-white font-medium">Card subscription</p>
                <p className="text-gray-400 text-xs mt-1">
                  ${subPrice}/mo via Stripe. Cancel anytime; you keep access through the end of the period.
                </p>
              </div>
              <span className="text-crypto-accent text-sm font-medium whitespace-nowrap">
                {subscribing === target ? 'Loading…' : 'Subscribe →'}
              </span>
            </div>
          </button>
        </div>
        )}
      </div>
    </div>
  );
};

export default UpgradePicker;
